import { useCallback, useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, KpiCard } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, TrendingUp, Truck, Package, Receipt } from "lucide-react";

const Money = ({ v }) => <span className="font-mono">{eur(v)}</span>;

const Flag = ({ ok, okText, badText, testId }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${ok ? "bg-primary/15 text-primary" : "bg-chart-3/20 text-chart-3"}`} data-testid={testId}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
    {ok ? okText : badText}
  </span>
);

/** Abschnitt C: Auswertung, Einkäufe, Steuern, Datenvollständigkeit, offene Carrierkosten. */
export default function FinanceStatement() {
  const { t } = useI18n();
  const nowY = new Date().getFullYear();
  const [tab, setTab] = useState("overview");
  const [year, setYear] = useState(String(nowY));
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(null);
  const [open, setOpen] = useState(null);
  const [docs, setDocs] = useState(null);

  const load = useCallback(() => {
    const p = { year };
    if (month) p.month = month;
    api.get("/finance/statement", { params: p }).then((r) => setData(r.data)).catch((e) => { setData(null); if (e?.response?.status !== 403) console.error(apiError(e)); });
    api.get("/finance/months", { params: { year } }).then((r) => setMonths(r.data.months)).catch(() => setMonths(null));
    api.get("/finance/open-carrier-costs").then((r) => setOpen(r.data)).catch(() => setOpen(null));
    api.get("/finance/document-status").then((r) => setDocs(r.data)).catch(() => setDocs(null));
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const cur = data?.currencies?.[0];
  const shipC = data?.completeness?.shipping_cost;
  const TABS = [["overview", "finc.tabOverview"], ["months", "finc.tabMonths"],
    ["purchases", "finc.tabPurchases"], ["tax", "finc.tabTax"], ["completeness", "finc.tabCompleteness"]];

  return (
    <div className="space-y-6" data-testid="finance-statement">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label>{t("finc.year")}</Label>
          <Input className="mt-1.5 w-28" value={year} onChange={(e) => setYear(e.target.value)} data-testid="finc-year" /></div>
        <div><Label>{t("finc.month")}</Label>
          <Input className="mt-1.5 w-24" placeholder="1-12" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="finc-month" /></div>
        <Button variant="outline" onClick={() => setMonth("")} data-testid="finc-reset">{t("finc.wholeYear")}</Button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`finc-tab-${k}`}
            className={`px-3 py-2 text-sm transition-colors ${tab === k ? "border-b-2 border-primary font-medium text-primary" : "text-muted-foreground hover:text-foreground"}`}>
            {t(label)}
          </button>
        ))}
      </div>

      {!data && <p className="text-sm text-muted-foreground" data-testid="finc-empty">{t("finc.noAccessOrData")}</p>}

      {data && tab === "overview" && cur && (
        <div className="space-y-4" data-testid="finc-overview">
          {/* Jede Währung wird eigenständig beziffert — Beträge werden nie addiert. */}
          {data.currencies.map((c) => (
            <div key={c.currency} className="space-y-4" data-testid={`finc-currency-${c.currency}`}>
              {data.currencies.length > 1 && <p className="text-sm font-medium">{c.currency}</p>}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard icon={TrendingUp} label={t("finc.netRevenue")} value={eur(c.revenue.net_revenue)} />
                <KpiCard icon={Package} label={t("finc.cogs")} value={eur(-c.operating_costs.cogs)} />
                <KpiCard icon={Truck} label={t("finc.carrierCost")} value={eur(-c.operating_costs.actual_carrier_cost)} />
                <KpiCard icon={Receipt} label={t("finc.cm1")} value={eur(c.contribution.cm1)} />
              </div>
              <div className="rounded-lg border border-border p-4 text-sm" data-testid="finc-contribution">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <strong>{t("finc.cmFulfillment")}</strong>
                  <Money v={c.contribution.cm_fulfillment} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{t("finc.cmFulfillmentFormula")}</p>
                {c.contribution.is_provisional && (
                  <p className="mt-2 text-xs text-chart-3" data-testid="finc-provisional">
                    {t("finc.provisional", { missing: shipC?.shipping_cost_missing_count || 0, provisional: shipC?.shipping_cost_provisional_count || 0 })}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <span>{t("finc.resultAfterWriteOffs")}</span>
                  <Money v={c.contribution.result_after_write_offs} />
                </div>
                <p className="text-xs text-muted-foreground">{t("finc.writeOffsSeparate")}: {eur(data.write_offs.total)}</p>
              </div>
            </div>
          ))}
          {data.currencies.length > 1 && (
            <p className="text-xs text-muted-foreground" data-testid="finc-multi-currency">{t("finc.multiCurrency")}</p>
          )}
        </div>
      )}

      {data && tab === "months" && (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="finc-months">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr>
              <Th>{t("finc.month")}</Th><Th right>{t("finc.netRevenue")}</Th><Th right>{t("finc.cogs")}</Th>
              <Th right>{t("finc.carrierCost")}</Th><Th right>{t("finc.cm1")}</Th><Th right>{t("finc.cmFulfillment")}</Th>
            </tr></thead>
            <tbody>
              {(months || []).map((m) => (
                <tr key={m.month} className="border-t border-border">
                  <Td mono>{m.month}{m.is_provisional ? " *" : ""}</Td>
                  <Td right><Money v={m.net_revenue} /></Td>
                  <Td right><Money v={-m.cogs} /></Td>
                  <Td right><Money v={-m.actual_carrier_cost} /></Td>
                  <Td right><Money v={m.cm1} /></Td>
                  <Td right><Money v={m.cm_fulfillment} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="p-3 text-xs text-muted-foreground">{t("finc.provisionalStar")}</p>
        </div>
      )}

      {data && tab === "purchases" && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="finc-purchases">
          {[["finc.goodsPurchases", data.purchases.goods], ["finc.packagingPurchases", data.purchases.packaging]].map(([label, p]) => (
            <div key={label} className="rounded-lg border border-border p-4 text-sm">
              <strong>{t(label)}</strong>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between"><span>{t("itx.net")}</span><Money v={p.net} /></div>
                <div className="flex justify-between"><span>{t("itx.tax")}</span><Money v={p.tax} /></div>
                <div className="flex justify-between font-medium"><span>{t("itx.gross")}</span><Money v={p.gross} /></div>
                <p className="text-xs text-muted-foreground">{t("finc.documents", { count: p.count })} · {t("finc.incomplete", { count: p.incomplete_documents })}</p>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground sm:col-span-2">{t("finc.purchaseNotCogs")}</p>
        </div>
      )}

      {data && tab === "tax" && (
        <div className="space-y-4 text-sm" data-testid="finc-tax">
          <div className="rounded-lg border border-border p-4">
            <strong>{t("finc.outputVat")}</strong>
            <div className="mt-2 space-y-1">
              {data.tax.output_vat.by_tax_code.map((b) => (
                <div key={b.tax_code} className="flex justify-between">
                  <span>{t(`tax.code.${b.tax_code}`, b.tax_code)} · {b.rate}%</span><Money v={b.tax} />
                </div>
              ))}
              <div className="flex justify-between border-t border-border pt-1 font-medium">
                <span>{t("common.total")}</span><Money v={data.tax.output_vat.tax} /></div>
            </div>
          </div>
          <div className="rounded-lg border border-border p-4">
            <strong>{t("finc.capturedInputTax")}</strong>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between"><span>{t("finc.goods")}</span><Money v={data.tax.input_tax.goods.tax} /></div>
              <div className="flex justify-between"><span>{t("finc.packaging")}</span><Money v={data.tax.input_tax.packaging.tax} /></div>
              <div className="flex justify-between"><span>{t("finc.carrier")}</span><Money v={data.tax.input_tax.carrier.tax} /></div>
              <div className="flex justify-between border-t border-border pt-1 font-medium">
                <span>{t("common.total")}</span><Money v={data.tax.input_tax.total_captured} /></div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("finc.inputTaxNote")}</p>
          </div>
          <div className="rounded-lg border border-border p-4" data-testid="finc-vat-summary">
            <div className="flex justify-between font-medium">
              <span>{t("finc.vatDifference")}</span><Money v={data.tax.vat_summary.vat_difference} /></div>
            <p className="mt-1 text-xs text-chart-3">{t("finc.vatDisclaimer")}</p>
            {data.tax.vat_summary.is_provisional && (
              <p className="text-xs text-chart-3" data-testid="finc-vat-provisional">{t("finc.vatProvisional")}</p>
            )}
          </div>
        </div>
      )}

      {data && tab === "completeness" && (
        <div className="space-y-4 text-sm" data-testid="finc-completeness">
          <div className="rounded-lg border border-border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong>{t("finc.shippingCompleteness")}</strong>
              <Flag ok={!!shipC?.is_shipping_cost_complete} okText={t("finc.allShippingCaptured")}
                badText={t("finc.shippingIncomplete")} testId="finc-shipping-flag" />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t("finc.shippingCounts", { captured: shipC?.shipping_cost_complete_count || 0, total: shipC?.fulfillment_count || 0, provisional: shipC?.shipping_cost_provisional_count || 0, missing: shipC?.shipping_cost_missing_count || 0 })}
            </p>
          </div>
          {docs && (
            <div className="rounded-lg border border-border p-4">
              <strong>{t("finc.incomingDocuments")}</strong>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {["goods", "packaging"].map((k) => (
                  <p key={k} className="text-xs text-muted-foreground">
                    {t(`finc.${k}`)}: {t("itx.status.COMPLETE")} {docs[k].COMPLETE} · {t("itx.status.PARTIAL")} {docs[k].PARTIAL} · {t("itx.status.MISSING")} {docs[k].MISSING}
                  </p>
                ))}
              </div>
              <Flag ok={data.completeness.input_tax_complete} okText={t("finc.allDocumentsComplete")}
                badText={t("finc.documentsIncomplete")} testId="finc-docs-flag" />
            </div>
          )}
          <div className="rounded-lg border border-border" data-testid="finc-open-carrier">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4">
              <strong>{t("finc.openCarrierCosts")}</strong>
              {open && <Flag ok={open.all_captured} okText={t("finc.allShippingCaptured")}
                badText={t("finc.openCarrierCount", { count: open.rows.length })} testId="finc-open-flag" />}
            </div>
            {open && open.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr>
                    <Th>{t("ord.title")}</Th><Th>{t("finc.shippedAt")}</Th><Th>{t("ship.carrier")}</Th>
                    <Th>{t("ship.reference")}</Th><Th>{t("common.status")}</Th><Th right>{t("itx.net")}</Th>
                    <Th>{t("finc.missingInfo")}</Th><Th></Th>
                  </tr></thead>
                  <tbody>
                    {open.rows.map((r) => (
                      <tr key={r.fulfillment_id} className="border-t border-border">
                        <Td mono>{r.order_number}</Td>
                        <Td>{(r.shipped_at || "").slice(0, 10)}</Td>
                        <Td>{r.carrier || "—"}</Td>
                        <Td mono>{r.shipping_reference || "—"}</Td>
                        <Td>{t(`itx.status.${r.status}`)}</Td>
                        <Td right>{r.cost_net === null ? t("finc.notCaptured") : eur(r.cost_net)}</Td>
                        <Td className="text-xs text-muted-foreground">
                          {r.missing.map((m) => t(`finc.missing.${m}`, m)).join(", ")}</Td>
                        <Td>
                          <Link to={`/auftraege/${r.order_id}`} className="text-xs underline"
                            data-testid={`finc-open-link-${r.fulfillment_id}`}>{t("finc.capture")}</Link></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
